/**
 * Turning one patient's record into what the overview screen shows (PHASE-0.md §2.4).
 *
 * Pure functions, separate from the component, for the reason `patientDisplay.ts` gives:
 * this is where the safety-critical decisions are — which facts are above the fold, what
 * a missing one reads as, and what is never reformatted — and those are worth testing
 * without a DOM.
 *
 * **The one decision worth reading before the code is {@link safetyPanel}'s five
 * entries.** `AGENTS.md` §5 requires *allergies, DNR, treatment limitation and weight*
 * visible without scrolling; `PHASE-0.md` §2.4 requires *allergier, DNR, isolering och
 * vikt*. The two lists differ by one item each way, and neither document is the one to
 * drop from — a treatment limitation is what may not be done and an isolation is what
 * must be done before entering the room — so the panel renders the **union**, five slots,
 * and every slot is always present. A slot with nothing in it says so in words: an empty
 * field reads as *no DNR* and *no DNR documented* is a different fact.
 *
 * **`t` is a parameter, not an import** (I18N.md §1). A module-level `t()` resolves once,
 * when the bundle is first evaluated, and freezes whatever language was selected then.
 *
 * Record content is never translated and never reformatted (AGENTS.md §3 rule 8, §7):
 * the allergen name, the isolation type, the treatment limitation, the weight's digits,
 * the frequency and the note text are all shown as the documenter wrote them. What this
 * module supplies is the label beside them, the unit after the weight, and the order.
 */

import type { TFunction } from 'i18next'

import { formatClinicalDateTime } from '../format/clinicalDate'
import { formatDose } from '../format/clinicalDose'
import { allergyBadge, guarded, type SafetyLevel } from './patientDisplay'
import type {
  PatientAllergy,
  PatientMedication,
  PatientNote,
  PatientOverview,
  PatientSafetyFlag,
} from './patientOverviewApi'
import type { IdentifierSystem, PatientSafety } from './patientRecord'

/** The lookup this screen needs. `common` first — see `patientDisplay.ts`. */
export type PatientOverviewTranslate = TFunction<['common', 'patient']>

/**
 * The flag types {@link safetyPanel} gives a slot of their own.
 *
 * Every other active flag lands in the sixth entry rather than being dropped, which is
 * what keeps a type added by a later migration visible on the screen.
 */
const PANEL_FLAG_TYPES = ['dnr', 'treatment-limitation', 'isolation', 'weight'] as const

/**
 * One line within a safety panel entry.
 *
 * A list rather than a single string because the record permits more than one flag of a
 * type — two treatment limitations taken at two admissions are both active, and showing
 * the first one only would hide a decision.
 */
export interface SafetyPanelRow {
  /** The fact in brief, in the documenter's words where there are any. */
  readonly value: string

  /** The reasoning behind the brief. Record content. Null when there is none. */
  readonly detail: string | null

  /** Who decided and when, for a decision. Null for an observation. */
  readonly attribution: string | null
}

/** One of the five facts that must be readable without scrolling, plus the rest. */
export interface SafetyPanelEntry {
  /** Stable key for React and for the tests. */
  readonly key: string
  readonly label: string

  /** Never empty. A slot with no flag holds one row saying so. */
  readonly rows: readonly SafetyPanelRow[]
  readonly level: SafetyLevel

  /**
   * Whether the rows are a fact the record holds, or the record saying nothing.
   *
   * The level alone cannot tell them apart: a documented weight and an undocumented DNR are both
   * `neutral`. The strip draws them differently since 2026-10-08 (major-incident review) — a
   * present hazard fills its slot, a quiet absence goes grey and short, and a reader finds what
   * applies before reading a word. `allergy-status-unknown` and *no known allergies* count as
   * documented: somebody asked.
   */
  readonly documented: boolean
}

/**
 * A practitioner's name in reading order, or that the record does not document one.
 *
 * `Given Family`, unlike {@link patientName}'s `Family, Given`: a list is sorted by
 * surname and a person referred to in a sentence is not. Same shape as
 * `ward/wardDisplay.ts`'s `responsibleDoctor`, with this screen's string.
 */
export function practitionerName(
  givenName: string | null,
  familyName: string | null,
  t: PatientOverviewTranslate,
): string {
  const given = givenName ?? ''
  const family = familyName ?? ''

  if (given === '' && family === '') {
    return t('patient:practitioner.notDocumented')
  }

  return `${given} ${family}`.trim()
}

/** Which series an identifier belongs to, in words. */
export function identifierLabel(system: IdentifierSystem, t: PatientOverviewTranslate): string {
  switch (system) {
    case 'personnummer':
      return t('patient:identifier.personnummer')
    case 'samordningsnummer':
      return t('patient:identifier.samordningsnummer')
    case 'reservnummer':
      return t('patient:identifier.reservnummer')
    case 'unknown-series':
      return t('patient:identifier.unknownSeries')
  }
}

/**
 * HL7 administrative gender in words, or that the record does not say.
 *
 * `unknown` is a recorded value and gets its own word: somebody looked and could not
 * say, which is not the same as nobody having asked.
 */
export function patientSex(sex: string | null, t: PatientOverviewTranslate): string {
  switch (sex) {
    case 'male':
      return t('patient:sex.male')
    case 'female':
      return t('patient:sex.female')
    case 'other':
      return t('patient:sex.other')
    case 'unknown':
      return t('patient:sex.unknown')
    case null:
      return t('common:value.notRecorded')
    default:
      // A code a later migration permitted. Shown as recorded rather than hidden.
      return sex
  }
}

/**
 * The badge input for one overview, so the header's badges are the ward list's badges.
 *
 * `safetyBadges` takes {@link PatientSafety} — five fields the two list screens carry as
 * columns and the overview carries as an allergy status plus a list of typed flags. This
 * is the one place that translation happens, so a patient's badges cannot read one way
 * in a search result and another on their own screen (PHASE-0.md §3.4).
 */
export function overviewSafety(
  overview: PatientOverview,
  t: PatientOverviewTranslate,
): PatientSafety {
  const isolation = overview.safetyFlags.find((flag) => flag.flagType === 'isolation')

  return {
    allergyStatus: overview.allergyStatus,
    dnr: overview.safetyFlags.some((flag) => flag.flagType === 'dnr'),

    // The isolation *type* is what the badge says, and the database requires a
    // non-empty value on an isolation flag (`safety_flag_value_present_when_required`,
    // migration 0005). If one arrives without it, the flag still has to show: an
    // isolation nobody can name is not the same as no isolation, and treating it as
    // the latter sends somebody into the room.
    isolation:
      isolation === undefined
        ? null
        : (isolation.value ?? t('patient:safety.isolationTypeNotRecorded')),

    isEstimatedIdentity: overview.isEstimatedIdentity,
    deceased: overview.death !== null,
    confidentialityMarked: overview.confidentialityMarked,
  }
}

/** A severity code in words. Null is **not** `mild` and says so. */
function severityWord(severity: string | null, t: PatientOverviewTranslate): string {
  switch (severity) {
    case 'mild':
      return t('patient:allergy.severityMild')
    case 'moderate':
      return t('patient:allergy.severityModerate')
    case 'severe':
      return t('patient:allergy.severitySevere')
    case null:
      // An allergy nobody graded is not a mild allergy. The query already refuses to
      // sort it below one (`WHEN a.severity IS NULL THEN 3`); this is the same fact in
      // words, and it is why there is no blank here.
      return t('patient:allergy.severityNotRecorded')
    default:
      return severity
  }
}

/**
 * One allergy as a line: the substance, its qualifiers, and the reaction.
 *
 * **The qualifiers are a punctuation-joined list, not an assembled sentence.** I18N.md
 * §8 forbids gluing translated words together into prose because the word order is the
 * translator's decision — and it is respected here: the substance, the qualifier list
 * and the reaction each occupy a slot in one complete message, so a language may put
 * them in any order. What is joined with a comma is a *list* of independently translated
 * adjectives, which is how a clinical record renders qualifiers in every language we
 * expect to meet.
 *
 * The substance and the reaction are record content and appear exactly as documented.
 * The severity slot is never empty — see {@link severityWord}.
 */
export function allergyLine(allergy: PatientAllergy, t: PatientOverviewTranslate): string {
  const qualifiers = [severityWord(allergy.severity, t)]

  if (allergy.criticality === 'high') {
    qualifiers.push(t('patient:allergy.criticalityHigh'))
  } else if (allergy.criticality === 'low') {
    qualifiers.push(t('patient:allergy.criticalityLow'))
  } else if (allergy.criticality === 'unable-to-assess') {
    qualifiers.push(t('patient:allergy.criticalityUnableToAssess'))
  }

  if (allergy.verification === 'unconfirmed') {
    // Only the unconfirmed case earns a word. A confirmed allergy is what the reader
    // already assumes a documented allergy to be, and labelling it would make the
    // qualifier list longer in the common case in order to say nothing.
    qualifiers.push(t('patient:allergy.unconfirmed'))
  }

  if (allergy.status === 'resolved') {
    qualifiers.push(t('patient:allergy.resolved'))
  }

  const substance = allergy.substance ?? t('patient:allergy.substanceNotRecorded')
  const joined = qualifiers.join(', ')

  return allergy.reaction === null
    ? t('patient:allergy.line', { substance, qualifiers: joined })
    : t('patient:allergy.lineWithReaction', {
        substance,
        qualifiers: joined,
        reaction: allergy.reaction,
      })
}

/** Who decided and when, or null when the flag records no decision. */
export function flagDecision(
  flag: PatientSafetyFlag,
  t: PatientOverviewTranslate,
): string | null {
  if (flag.decidedByGivenName === null && flag.decidedByFamilyName === null) {
    return null
  }

  const name = practitionerName(flag.decidedByGivenName, flag.decidedByFamilyName, t)

  // `safety_flag_decision_attributed` (migration 0005) requires a decider and a time
  // together on a DNR and a treatment limitation, so a missing time here is not a shape
  // the record can hold. Said rather than hidden, because the alternative is a decision
  // that looks undated for no visible reason.
  const decidedAt = flag.decidedAt
  const time =
    decidedAt === null
      ? t('common:value.notRecorded')
      : guarded(() => formatClinicalDateTime(decidedAt), t)

  // The title travels in the same message as the name rather than being glued in front
  // of it: `läkare Jonas Vikström` is Swedish word order, and a language that puts the
  // title after the name has to be able to.
  return flag.decidedByRoleTitle === null
    ? t('patient:safety.decidedBy', { name, time })
    : t('patient:safety.decidedByWithTitle', {
        title: flag.decidedByRoleTitle,
        name,
        time,
      })
}

/**
 * A flag type in words, or the code itself.
 *
 * The seven the schema permits get a label; anything a later migration adds is shown as
 * the code it is. **Never hidden** — an unrecognised safety flag that does not render is
 * a safety flag that does not exist as far as the clinician is concerned, and the
 * database is where the vocabulary is closed, not here.
 */
export function flagLabel(flagType: string, t: PatientOverviewTranslate): string {
  switch (flagType) {
    case 'dnr':
      return t('patient:safety.dnr')
    case 'treatment-limitation':
      return t('patient:safety.treatmentLimitation')
    case 'isolation':
      return t('patient:safety.isolation')
    case 'infection-risk':
      return t('patient:safety.infectionRisk')
    case 'fall-risk':
      return t('patient:safety.fallRisk')
    case 'allergy-status-unknown':
      return t('patient:safety.allergyStatusUnknown')
    case 'weight':
      return t('patient:safety.weight')
    default:
      return flagType
  }
}

/** The rows for one flag type's slot, or null when the patient has no such flag. */
function flagRows(
  overview: PatientOverview,
  flagType: string,
  t: PatientOverviewTranslate,
  render: (flag: PatientSafetyFlag) => string,
): readonly SafetyPanelRow[] | null {
  const flags = overview.safetyFlags.filter((flag) => flag.flagType === flagType)

  if (flags.length === 0) {
    return null
  }

  return flags.map((flag) => ({
    value: render(flag),
    detail: flag.note,
    attribution: flagDecision(flag, t),
  }))
}

/** The brief as documented, or that the flag carries none. */
function flagValue(flag: PatientSafetyFlag, t: PatientOverviewTranslate): string {
  return flag.value ?? t('patient:safety.valueNotRecorded')
}

/**
 * The five facts that must be readable without scrolling, in the order they are read,
 * plus every other active flag.
 *
 * **All five entries are always returned.** That is the whole point: a panel that omits
 * the DNR slot when there is no DNR is indistinguishable from a panel whose DNR slot
 * failed to render, and a clinician scanning for *Ingen HLR* would find nothing either
 * way. So an absent flag is a sentence — *Inget stallningstagande dokumenterat* — and
 * the level tells the reader how much that absence matters.
 *
 * The weight's absence is a `warning` rather than a `neutral` because a patient with no
 * documented weight is one whose weight-based doses cannot be checked against anything.
 * That is a statement about the record, not a calculation (AGENTS.md §3 rule 5).
 */
export function safetyPanel(
  overview: PatientOverview,
  t: PatientOverviewTranslate,
): readonly SafetyPanelEntry[] {
  const entries: SafetyPanelEntry[] = []

  // 1. Allergies. The rows the record holds, and the status statement when there are
  //    none to list — or *as well as* them, when a documented allergy sits beside an
  //    unknown-status flag and the list is therefore not known to be complete.
  const badge = allergyBadge(overview.allergyStatus, t)
  const active = overview.allergies.filter((allergy) => allergy.status === 'active')
  const resolved = overview.allergies.filter((allergy) => allergy.status === 'resolved')

  // Active before resolved, each group keeping the severity order the query applied.
  // A `no-known-allergies` row names no substance and is not a line: it is the
  // assertion, and the badge above is where it is said.
  const allergyRows = [...active, ...resolved].map((allergy) => ({
    value: allergyLine(allergy, t),
    detail: null,
    attribution: null,
  }))

  if (allergyRows.length === 0 || overview.allergyStatus !== 'active') {
    allergyRows.push({ value: badge.label, detail: null, attribution: null })
  }

  entries.push({
    key: 'allergies',
    label: t('patient:safety.allergies'),
    rows: allergyRows,
    level: badge.level,
    documented: overview.allergyStatus !== 'undocumented',
  })

  // 2–5. DNR, treatment limitation, isolation, weight. AGENTS.md §5's order, with
  //      isolation in the place PHASE-0.md §2.4 asks for it.
  const slots: readonly {
    readonly flagType: (typeof PANEL_FLAG_TYPES)[number]
    readonly label: string
    readonly absent: string
    readonly present: SafetyLevel
    readonly missing: SafetyLevel
    readonly render: (flag: PatientSafetyFlag) => string
  }[] = [
    {
      flagType: 'dnr',
      label: t('patient:safety.dnr'),
      absent: t('patient:safety.dnrNotDocumented'),
      present: 'critical',
      missing: 'neutral',
      render: (flag) => flagValue(flag, t),
    },
    {
      flagType: 'treatment-limitation',
      label: t('patient:safety.treatmentLimitation'),
      absent: t('patient:safety.treatmentLimitationNotDocumented'),
      present: 'critical',
      missing: 'neutral',
      render: (flag) => flagValue(flag, t),
    },
    {
      flagType: 'isolation',
      label: t('patient:safety.isolation'),
      absent: t('patient:safety.isolationNotDocumented'),
      present: 'warning',
      missing: 'neutral',
      render: (flag) => flagValue(flag, t),
    },
    {
      flagType: 'weight',
      label: t('patient:safety.weight'),
      absent: t('patient:safety.weightNotDocumented'),
      present: 'neutral',
      missing: 'warning',

      // The unit is the interface's, the number is the record's. A weight arrives as
      // text and it stays text: this appends `kg` through a catalogue message and
      // touches nothing else, because reformatting the digits would mean this screen
      // had decided what `62` means (AGENTS.md §5, migration 0005's column comment).
      render: (flag) =>
        flag.value === null
          ? t('patient:safety.valueNotRecorded')
          : t('patient:safety.weightKg', { weight: flag.value }),
    },
  ]

  for (const slot of slots) {
    const rows = flagRows(overview, slot.flagType, t, slot.render)

    entries.push({
      key: slot.flagType,
      label: slot.label,
      rows: rows ?? [{ value: slot.absent, detail: null, attribution: null }],
      level: rows === null ? slot.missing : slot.present,
      documented: rows !== null,
    })
  }

  // 6. Everything else that is active: fall risk, infection risk, unknown allergy
  //    status, and any type a later migration adds. Omitted when there is none —
  //    unlike the five above, whose absence is itself the fact.
  // Widened to `string[]` for the membership test rather than narrowing `flagType` to
  // the tuple: the flag type is open (see `KNOWN_FLAG_TYPES`) and a cast in the other
  // direction would be claiming it is not.
  const panelled: readonly string[] = PANEL_FLAG_TYPES
  const others = overview.safetyFlags.filter((flag) => !panelled.includes(flag.flagType))

  if (others.length > 0) {
    entries.push({
      key: 'other',
      label: t('patient:safety.other'),
      rows: others.map((flag) => ({
        value:
          flag.value === null
            ? flagLabel(flag.flagType, t)
            : t('patient:safety.otherFlag', {
                label: flagLabel(flag.flagType, t),
                value: flag.value,
              }),
        detail: flag.note,
        attribution: flagDecision(flag, t),
      })),
      level: 'warning',
      documented: true,
    })
  }

  return entries
}

/**
 * The dose, through the single shared dose formatter.
 *
 * Wrapped in {@link guarded} rather than allowed to throw, which is the one place that
 * choice is arguable: `formatDose` throws precisely so that a dose which could be
 * misread is never shown, and *Kan inte visas* honours that — it says a dose is
 * recorded and cannot be displayed, which is not a dose and cannot be mistaken for one.
 * Letting it propagate would take the allergy panel down with it.
 */
export function medicationDose(
  medication: PatientMedication,
  t: PatientOverviewTranslate,
): string {
  return guarded(() => formatDose(medication.doseAmount, medication.doseUnit), t)
}

/**
 * How often, and the ceiling when the order is vid behov.
 *
 * The frequency is the prescriber's words and is never parsed. The ceiling is the
 * prescriber's number and is never computed from the dose and the frequency — that
 * computation is dose calculation (AGENTS.md §3 rule 5) — so a vid behov order that
 * somehow carries no ceiling says exactly that, loudly, rather than being shown as an
 * ordinary order. `medication_request_prn_has_max_dose` (migration 0006) means the
 * record cannot hold one, which is why the string names the omission.
 */
export function medicationSchedule(
  medication: PatientMedication,
  t: PatientOverviewTranslate,
): string {
  if (!medication.isPrn) {
    return medication.frequency
  }

  return medication.prnMaxDose24h === null
    ? t('patient:medication.prnWithoutMaximum', { frequency: medication.frequency })
    : t('patient:medication.prn', {
        frequency: medication.frequency,
        maximum: medication.prnMaxDose24h,
      })
}

/** Strength and dose form as labelled on the package. Both verbatim. */
export function medicationPreparation(
  medication: PatientMedication,
  t: PatientOverviewTranslate,
): string {
  return t('patient:medication.preparation', {
    strength: medication.strength,
    form: medication.doseForm,
  })
}

/**
 * A note's language by name, in the interface language — *spanska* under Swedish, *sueco* under
 * Spanish — for the line that says a note is written in another language (DECISIONS.md #54).
 *
 * The browser's own `Intl.DisplayNames`, which is offline and needs no catalogue entry per language,
 * so a note in a language this build has no catalogue for is still named. Not a clinical value, so
 * AGENTS.md §5's rule about locale formatting does not reach it. The bare subtag where the browser
 * cannot name it: shown as stored rather than hidden.
 */
export function noteLanguageName(language: string, interfaceLanguage: string): string {
  try {
    return displayNames(interfaceLanguage).of(language) ?? language
  } catch {
    return language
  }
}

/** One formatter per interface language, not one per note per render. */
const displayNamesCache = new Map<string, Intl.DisplayNames>()

function displayNames(interfaceLanguage: string): Intl.DisplayNames {
  let names = displayNamesCache.get(interfaceLanguage)

  if (names === undefined) {
    names = new Intl.DisplayNames([interfaceLanguage], { type: 'language' })
    displayNamesCache.set(interfaceLanguage, names)
  }

  return names
}

/** A note type in words, or the code itself for a type this interface has not met. */
export function noteTypeLabel(noteType: string, t: PatientOverviewTranslate): string {
  switch (noteType) {
    case 'daily':
      return t('patient:notes.typeDaily')
    case 'admission':
      return t('patient:notes.typeAdmission')
    case 'discharge':
      return t('patient:notes.typeDischarge')
    default:
      // `note_type` is open on purpose (migration 0007): note types are local practice.
      // Showing the code beats hiding the note.
      return noteType
  }
}

/** Who wrote the note and when it is clinically true. */
export function noteAttribution(note: PatientNote, t: PatientOverviewTranslate): string {
  const name = practitionerName(note.authorGivenName, note.authorFamilyName, t)
  const time = guarded(() => formatClinicalDateTime(note.effectiveAt), t)

  return note.authorRoleTitle === null
    ? t('patient:notes.attribution', { name, time })
    : t('patient:notes.attributionWithTitle', {
        title: note.authorRoleTitle,
        name,
        time,
      })
}

/**
 * When the note was written, if a reader would see a different time from
 * {@link PatientNote.effectiveAt}. Null otherwise.
 *
 * **The comparison is between the rendered strings, deliberately.** The question this
 * answers is not whether the two timestamps differ — they almost always differ by
 * milliseconds — but whether a clinician looking at the screen would see two different
 * times. `YYYY-MM-DD HH:mm` is the only precision shown (I18N.md §3), so it is the only
 * precision the comparison may use.
 *
 * Retroactive documentation is normal and not an edge case (AGENTS.md §4). Saying so is
 * what makes it visible rather than silent.
 */
export function noteRecordedLater(
  note: PatientNote,
  t: PatientOverviewTranslate,
): string | null {
  try {
    const effective = formatClinicalDateTime(note.effectiveAt)
    const recorded = formatClinicalDateTime(note.recordedAt)

    return effective === recorded ? null : t('patient:notes.recordedLater', { time: recorded })
  } catch {
    // One unreadable timestamp must not take the note's text off the screen. The note
    // itself still renders; `noteAttribution` is where the failure is reported.
    return null
  }
}
